/**
 * PRD-1628: what a dataset upload (POST /datasets/{id}/files) stored and what it
 * rejected.
 *
 * Contract (PRD-1610):
 *   201 → FileUploadResponse[]                     every file was stored
 *   207 → { uploaded: FileUploadResponse[],
 *           rejected: [{ file_name, reason }] }    the good files go on as normal
 *   413/415 → every file rejected; `detail` names each one (thrown as an error)
 *
 * Not behind a flag: the backend sends the 207 unflagged, so hiding this notice
 * would drop the rejected files from the upload's result without a word.
 */

/** Both response shapes as `{ uploaded, rejected }`. */
export function readUploadOutcome(response) {
  if (Array.isArray(response)) return { uploaded: response, rejected: [] };
  return {
    uploaded: Array.isArray(response?.uploaded) ? response.uploaded : [],
    rejected: Array.isArray(response?.rejected) ? response.rejected : [],
  };
}

/** A 413/415: the server refused every file in the request (size or content). */
export function isUploadRejected(error) {
  return error?.status === 413 || error?.status === 415;
}

/**
 * Uploads one file on its own (the "separate datasets" loops). A rejection is
 * an outcome, not a failure, so the loop goes on to the next file; any other
 * error is rethrown. With one file the 415 `detail` is just the reason, so the
 * file's name is added here.
 */
export async function uploadSingleFile(file, upload) {
  try {
    return readUploadOutcome(await upload());
  } catch (error) {
    if (!isUploadRejected(error)) throw error;
    return { uploaded: [], rejected: [{ file_name: file.name, reason: error.message }] };
  }
}

/** Several outcomes (or raw responses) as one `{ uploaded, rejected }`. */
export function combineUploadOutcomes(outcomes) {
  return outcomes.map(readUploadOutcome).reduce(
    (all, o) => ({ uploaded: [...all.uploaded, ...o.uploaded], rejected: [...all.rejected, ...o.rejected] }),
    { uploaded: [], rejected: [] },
  );
}

/** "2 uploaded, 1 rejected — fake_photo.jpg: Upload is not a readable image", or
 *  null when nothing was rejected. Each rejection reads like the 415 detail. */
export function rejectedUploadNotice(response) {
  const { uploaded, rejected } = readUploadOutcome(response);
  if (rejected.length === 0) return null;
  const files = rejected.map((r) => `${r.file_name}: ${r.reason}`).join('; ');
  return `${uploaded.length} uploaded, ${rejected.length} rejected — ${files}`;
}

/**
 * Shows the rejected-files notice, if there is one, and says whether it did.
 * Alert when some files went through, error when none did. It stays up until
 * closed: it lists what to fix, too much to read in a 5s toast.
 */
export function notifyRejectedUploads(response, onShowNotification) {
  const notice = rejectedUploadNotice(response);
  if (!notice) return false;
  const type = readUploadOutcome(response).uploaded.length > 0 ? 'alert' : 'error';
  onShowNotification?.(notice, type, { duration: 0 });
  return true;
}
